// Reports.jsx
import { useState, useEffect } from 'react';
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAfter,
  doc,
  deleteDoc,
  setDoc
} from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import * as theme from '../../theme';
import Button from "@mui/material/Button";
import TextField from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ErrorDisplay from '../../components/ErrorDisplay';
import TruCard from '../../components/TruCard';
import { Box } from '@mui/material';
import FileUploadLink from '../../components/fileUpload/FileUploadLink';
import { useNavigate } from 'react-router';

export default function Reports() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [sessionDate, setSessionDate] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const clearError = () => setError('');

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      setError('Access denied.');
      return;
    }
    fetchSessions();
  }, [currentUser]);

  const fetchSessions = async (loadMore = false) => {
    setLoading(true);
    try {
      let q = query(
        collection(db, 'oneOffReports'),
        orderBy('dateCreated', 'desc'),
        limit(20)
      );
      if (loadMore && lastVisible) {
        q = query(q, startAfter(lastVisible));
      }
      const querySnapshot = await getDocs(q);
      const newSessions = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSessions(prev => loadMore ? [...prev, ...newSessions] : newSessions);
      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1] || null);
      setHasMore(querySnapshot.docs.length === 20);
    } catch (err) {
      setError('Failed to fetch sessions: ' + err.message);
    }
    setLoading(false);
  };

  const handleCreateSession = async () => {
    if (!firstName || !lastName || !sessionDate) {
      setError('Missing required fields.');
      return;
    }
    setLoading(true);
    const sessionId = uuidv4();
    const timestamp = dayjs().toISOString();
    const sessionTimestamp = sessionDate.toISOString();

    try {
      await setDoc(doc(db, 'oneOffReports', sessionId), {
        firstName,
        lastName,
        sessionDate: sessionTimestamp,
        brand: 'RenuHealth',
        dateCreated: timestamp,
        dateModified: timestamp,
        reports: {},
        reportData: {}
      });

      // Clear states and refresh list
      setFirstName('');
      setLastName('');
      setSessionDate(null);
      fetchSessions();
    } catch (err) {
      setError('Session creation failed: ' + err.message);
    }
    setLoading(false);
  };

  const handleDelete = async (sessionId) => {
    try {
      await deleteDoc(doc(db, 'oneOffReports', sessionId));
      fetchSessions();
    } catch (err) {
      setError('Delete failed: ' + err.message);
    }
  };

  return (
    <div style={theme.Container.basic}>
      <div style={{ ...theme.Heading, marginBottom: 20 }}>RenuHealth Reports</div>
      <ErrorDisplay message={error} type="error" clearMessage={clearError} />

      {/* Create Session Section */}
      <TruCard title="New Session" styles={{ marginBottom: 20, marginTop: 20 }}>
        <TextField
          fullWidth
          label="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          sx={{ mt: 2 }}
        />
        <TextField
          fullWidth
          label="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          sx={{ mt: 2 }}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Session Date"
            value={sessionDate}
            onChange={setSessionDate}
            sx={{ mt: 2, width: '100%' }}
          />
        </LocalizationProvider>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateSession}
          disabled={loading}
          sx={{ mt: 2 }}
          fullWidth
        >
          {loading ? 'Creating...' : 'Create Session'}
        </Button>
      </TruCard>

      {/* Sessions List */}
      <TruCard title="Uploaded Sessions">
        {sessions.length === 0 && <div>No sessions yet.</div>}
        {sessions.map(s => (
          <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FileUploadLink
              label={`${s.firstName} ${s.lastName}`}
              content={dayjs(s.sessionDate).format('MM/DD/YYYY')}
              onClick={() => navigate(`/admin/reports/session/${s.id}`)}
            />
            <Button 
              variant="contained" 
              color="secondary" 
              onClick={() => handleDelete(s.id)}
              sx={{ ml: 2, width: 'fit-content' }}
            >
              Delete
            </Button>
          </Box>
        ))}
        {hasMore && (
          <Button onClick={() => fetchSessions(true)} disabled={loading} sx={{ mt: 2 }}>
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        )}
      </TruCard>
    </div>
  );
}